import { FleeceClient } from '@fleece/client';
import { config } from './config';
import { classifyProbeFailure, type Connection, type ProbeOutcome } from './connection';

/**
 * Builds the console's handle on a service.
 *
 * A factory rather than a module singleton: which service the console talks to is chosen
 * by the visitor at runtime and can change without a reload, so a client fixed at import
 * time cannot express it. Components reach this through `useApi()`, never by building a
 * client themselves.
 *
 * It is the same typed client the service's own tests drive, so every response is revived
 * and checked.
 */
export function createApi(connection: Connection): FleeceClient {
  return new FleeceClient({ baseUrl: connection.apiUrl, token: connection.token, timeoutMs: config.requestTimeoutMs });
}

/**
 * Checks a candidate connection before the console commits to it.
 *
 * Two calls, because one cannot answer both questions. `/ping` is public, so it proves the
 * service is reachable without needing a token — separating "nothing answered" from
 * "something answered and refused you". Then an authenticated call, whose 401 is the only
 * way to tell a missing or wrong token from a good one. Listing accounts is the cheapest
 * call behind the token: one small table, and no market-data key needed, so a service
 * without Alpaca credentials still connects.
 *
 * Catching a typo here is the point. A wrong URL that gets stored instead surfaces later
 * as every panel failing at once, which looks like a broken service rather than a
 * mistyped host.
 */
export async function probeConnection(connection: Connection): Promise<ProbeOutcome> {
  const client = createApi(connection);
  try {
    await client.ping();
  } catch (error) {
    // Never `needs-token`: /ping is public, so a failure here is transport, or not Fleece.
    return classifyProbeFailure(error, connection.token !== undefined) === 'unreachable' ? 'unreachable' : 'error';
  }

  try {
    await client.listAccounts({});
    return 'ok';
  } catch (error) {
    return classifyProbeFailure(error, connection.token !== undefined);
  }
}
